import Decimal from 'decimal.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { ScrollSync, ScrollSyncPane } from 'react-scroll-sync';

import { StyledHeading, StyledArrow } from '../../../styles/sharedStyles';
import { StyledHeadingCheckbox } from '../../atoms/SettlementsDoneElement/StyledHeadingCheckbox';
import { StyledText } from '../../atoms/Text/StyledText';
import { StyledScrollHeaderPayAll } from '../../atoms/UserPanelAllTrainerPay/StyledScrollHeaderPayAll';
import { StyledFlexBoxWrapper } from './OpenElement.styles';
import { StyledPayAllElement } from '../../atoms/UserPanelTrainerPay/StyledPayAllElement';
import { StyledPayColoredWrapper } from '../../atoms/UserPanelTrainerPay/StyledPayColoredWrapper';

import { getLang } from '../../../strings';
import useScrollBar from '../../../logic/hooks/useScrollBar';
import AllTrainerPayPopup from '../../molecules/AllTrainerPayPopup/AllTrainerPayPopup';
import TableHeader from './TableHeader';
import MonthSummary from './MonthSummary';
import ActionButtons from './ActionButtons';
import TrainersList from './TrainersList';

const OpenElement = ({
    defaultOpen,
    triggerParentScroll,
    keyElement,
    val,
    firstElement,
    trigger2,
    refreshData,
}) => {
    const [showCheckboxes, toggleShowCheckboxes] = useToggle(false);
    const [isOpen, toggleIsOpen] = useToggle(defaultOpen ?? false);
    const [isOpenPayPopup, toggleIsOpenPayPopup] = useToggle(false);
    const [checkedTrainers, setCheckedTrainers] = useState([]);

    const { isScrollBar, scrollElement, trigger } = useScrollBar(true, true);

    useEffect(() => {
        if (isOpen) {
            trigger2?.();
            trigger?.();
            triggerParentScroll?.();
        }
    }, [isOpen, trigger2, trigger, triggerParentScroll]);

    const isAtLeastOneUnpaid = useMemo(
        () =>
            val &&
            Object.values(val).some(
                (t) =>
                    (!t.salary?.paid || t.salary?.paid !== 1) &&
                    new Decimal(t.pay).gt(0) &&
                    !t.is_admin
            ),
        [val]
    );

    const handlePay = useCallback(
        (trainers) => {
            setCheckedTrainers([]);
            if (!trainers.some((t) => !t.isPaid)) {
                toggleShowCheckboxes(false);
            }
            if (trigger2) {
                trigger2();
            }
            if (refreshData) {
                refreshData();
            }
        },
        [toggleShowCheckboxes, trigger2, refreshData]
    );

    const onChangeCheckedTrainers = useCallback((trainer) => {
        setCheckedTrainers((prev) => {
            if (prev.some((t) => t.id === trainer.id)) {
                return prev.filter((t) => t.id !== trainer.id);
            } else {
                const t = {
                    id: trainer.id,
                    name: trainer.name,
                    salary: trainer.salary,
                    maxAmountToPay: trainer.pay,
                };
                return [...prev, t];
            }
        });
    }, []);

    return (
        <StyledPayColoredWrapper isfirstchild={firstElement} isopen={isOpen}>
            <StyledHeading onClick={toggleIsOpen}>
                <StyledText
                    hasdeclaredfontsize="22px"
                    hasdeclaredfontweight="700"
                    hasdeclaredtextalign="center"
                    hasdeclaredtexttransform="uppercase"
                    hasdeclaredfontcolor="#6786BE"
                >
                    {(() => {
                        const [year, month] = keyElement.split('-'),
                            prettyMonth = new Date(year, month - 1).toLocaleString(getLang(), {
                                month: 'long',
                            });

                        return `${prettyMonth} ${year}`.toLocaleUpperCase();
                    })()}
                </StyledText>
                <StyledHeadingCheckbox>
                    <StyledArrow isopen={isOpen} />
                </StyledHeadingCheckbox>
            </StyledHeading>
            {isOpen && (
                <StyledPayAllElement>
                    <ScrollSync>
                        <>
                            <StyledFlexBoxWrapper $calcWidth={showCheckboxes}>
                                <ScrollSyncPane>
                                    <StyledScrollHeaderPayAll
                                        $isscrollbar={isScrollBar}
                                        ref={scrollElement}
                                        calcwidth={showCheckboxes}
                                    >
                                        <TableHeader />
                                    </StyledScrollHeaderPayAll>
                                </ScrollSyncPane>
                            </StyledFlexBoxWrapper>
                            <TrainersList
                                val={val}
                                showCheckboxes={showCheckboxes}
                                keyElement={keyElement}
                                onChangeCheckedTrainers={onChangeCheckedTrainers}
                                checkedTrainers={checkedTrainers}
                            />
                        </>
                    </ScrollSync>
                    <ActionButtons
                        isAtLeastOneUnpaid={isAtLeastOneUnpaid}
                        showCheckboxes={showCheckboxes}
                        toggleShowCheckboxes={toggleShowCheckboxes}
                        toggleIsOpenPayPopup={toggleIsOpenPayPopup}
                        checkedTrainers={checkedTrainers}
                    />
                    <MonthSummary val={val} />
                </StyledPayAllElement>
            )}
            {isOpenPayPopup ? (
                <AllTrainerPayPopup
                    trainers={checkedTrainers}
                    closePopup={toggleIsOpenPayPopup}
                    month={keyElement}
                    handlePay={handlePay}
                />
            ) : null}
        </StyledPayColoredWrapper>
    );
};

export default OpenElement;
